import { Component, input, inject, effect, computed } from '@angular/core';
import { Store } from '@ngxs/store';
import { GenericTreeService, GENERIC_TREE_STATE_NAME } from '../services/generic-tree.entity.service';
import { GenericTreeEntity } from '../generic-tree.entity';
import { CommonModule } from '@angular/common';
import { GenericTreeNodeComponent } from '../generic-tree-node/generic-tree-node.component';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { Update } from '../../core/state/entity.state';
import { GenericTreeDragDropService } from '../services/generic-tree-drag-drop.service';
import { concat, Observable } from 'rxjs';

@Component({
  selector: 'pixl-generic-tree-editor',
  standalone: true,
  imports: [CommonModule, GenericTreeNodeComponent, DragDropModule, TranslocoModule],
  templateUrl: './generic-tree-editor.component.html',
  styleUrl: './generic-tree-editor.component.scss'
})
export class GenericTreeEditorComponent {
  consumerId = input.required<string>();
  subjectId = input.required<string>();

  private genericTreeService = inject(GenericTreeService);
  private store = inject(Store);
  public dragDropService = inject(GenericTreeDragDropService);

  public readonly rootListId = 'root-list';

  // Watch the entire state slice
  private state = toSignal(this.store.select(state => state[GENERIC_TREE_STATE_NAME]));

  // Computed signal to get root nodes for the selected consumer and subject
  rootNodes = computed(() => {
    const cid = this.consumerId();
    const sid = this.subjectId();
    const currentState = this.state();

    if (!currentState || !currentState.entities) {
      return [];
    }

    const entities = currentState.entities as Record<string, GenericTreeEntity>;

    return Object.values(entities)
      .filter(node => node.consumerId === cid && node.subjectId === sid && node.root)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  });

  constructor() {
    // Reload when inputs change
    effect(() => {
      const cid = this.consumerId();
      const sid = this.subjectId();
      if (cid && sid) {
        this.genericTreeService.ensureLoaded({
          'filter[consumerId]': cid,
          'filter[subjectId]': sid,
          'page[size]': 1000
        });
      }
    });
  }

  drop(event: CdkDragDrop<GenericTreeEntity[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.processUpdates(event.container.data);
    }
    else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );

      const movedNode = event.container.data[event.currentIndex];
      // Optimistic update for moved node
      const updatedMovedNode = {
        ...movedNode,
        parentId: null, // undefined/null allows it to be root if backend handles it
        root: true,
        sortOrder: event.currentIndex
      } as GenericTreeEntity;

      this.store.dispatch(new Update(updatedMovedNode));
      const updateObs = this.genericTreeService.update(updatedMovedNode);

      // Get updates for other nodes, excluding the moved one (already handled)
      const siblings = event.container.data.filter(n => n.id !== movedNode.id);
      const sortUpdates = this.processUpdates(siblings, false);

      concat(updateObs, ...sortUpdates).subscribe();
    }
  }

  private processUpdates(nodes: GenericTreeEntity[], execute = true): Observable<GenericTreeEntity>[] {
    const updates: Observable<GenericTreeEntity>[] = [];
    nodes.forEach((node, index) => {
      if (node.sortOrder !== index) {
        const updatedNode = { ...node, sortOrder: index };
        this.store.dispatch(new Update(updatedNode));
        updates.push(this.genericTreeService.update(updatedNode));
      }
    });

    if (execute && updates.length > 0) {
      concat(...updates).subscribe();
    }

    return updates;
  }

  trackById(index: number, item: GenericTreeEntity): string {
    return item.id;
  }
}
